import { useEffect, useRef, useState } from 'react';

interface PhotoMetadata { photoUrl: string; attributions: { displayName: string; uri: string }[] }

function safePhotoUrl(value: unknown): value is string {
  if (typeof value !== 'string' || !value.startsWith('/placephoto?') || value.length > 8192 || value.includes('#')) return false;
  const params = new URLSearchParams(value.slice('/placephoto?'.length));
  return /^[A-Za-z0-9_-]{1,255}$/.test(params.get('placeId') || '')
    || /^places\/[A-Za-z0-9_-]{1,255}\/photos\/[A-Za-z0-9_-]{1,8000}$/.test(params.get('name') || '');
}

function safeAttribution(value: unknown): string | undefined {
  try {
    const url = new URL(String(value || ''));
    if (url.protocol === 'https:' && !url.username && !url.password && /(^|\.)google\.[a-z.]+$/i.test(url.hostname)) return url.href;
  } catch { /* Plain-text attribution when the provider supplies no safe link. */ }
}

/** Resolve expiring Google photo references on demand, never from persisted tokens. */
export function PlacePhoto({ url, name, className }: { url: string; name: string; className: string }) {
  const container = useRef<HTMLElement>(null);
  const [photo, setPhoto] = useState<PhotoMetadata | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setPhoto(null);
    setUnavailable(false);
    const load = async () => {
      try {
        if (!url.startsWith('/placephoto?') || url.length > 8192) throw new Error('Invalid photo path');
        const params = new URLSearchParams(url.slice('/placephoto?'.length));
        const placeId = params.get('placeId') || (params.get('name') || '').match(/^places\/([A-Za-z0-9_-]+)\/photos\//)?.[1];
        if (!placeId || !/^[A-Za-z0-9_-]{1,255}$/.test(placeId)) throw new Error('Invalid place ID');
        const response = await fetch(`/placephoto?placeId=${encodeURIComponent(placeId)}&format=json`, { signal: controller.signal, cache: 'no-store', headers: { accept: 'application/json' } });
        if (!response.ok) throw new Error('Photo unavailable');
        const data = await response.json();
        if (!safePhotoUrl(data.photoUrl)) throw new Error('No photo');
        if (!controller.signal.aborted) setPhoto({ photoUrl: data.photoUrl, attributions: Array.isArray(data.attributions) ? data.attributions : [] });
      } catch {
        if (!controller.signal.aborted) setUnavailable(true);
      }
    };
    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== 'undefined' && container.current) {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer?.disconnect(); void load(); }
      }, { rootMargin: '150px' });
      observer.observe(container.current);
    } else void load();
    return () => { controller.abort(); observer?.disconnect(); };
  }, [url]);

  return <figure ref={container} style={{ margin: 0, minHeight: 24 }}>
    {unavailable ? <figcaption>Destination photo unavailable. Open in Google Maps to view more.</figcaption> : photo ? <>
      <img className={className} src={photo.photoUrl} alt={name} loading="lazy" onError={() => setUnavailable(true)} />
      {photo.attributions.length > 0 ? <figcaption>
        Photo: {photo.attributions.map((author, index) => {
          const href = safeAttribution(author.uri);
          return <span key={index}>{index > 0 ? ', ' : ''}{href
            ? <a href={href} target="_blank" rel="noreferrer">{author.displayName}</a>
            : author.displayName}</span>;
        })}
      </figcaption> : null}
    </> : <figcaption role="status">Loading destination photo…</figcaption>}
  </figure>;
}
